/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import {
	CustomBlock,
	ElementName,
	MediaPlacement,
	PreviewBlock,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';
import {
	CommonCardWrapperStyles as CommonCardWrapper,
	EmptyAndSmallCardWrapper as SmallCardWrapper,
	useTrackedSmartCardState,
} from '@atlassian/company-hub-common';
import {
	CustomImageUploadRenderer,
	getImageTypeToShow,
} from '@atlassian/company-hub-common/media-components';

import { CardSize } from '../../../../../../types';
import { CardErrorState } from '../../../error-states';
import { type CommonCardProps } from '../../../types';

const titleBlockImagePaddingStyles = css({
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
});

const titleBlockUnauthorizedStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		display: 'none',
	},
	// ensure "Connect your account" link for unauth 3p links is boosted
	// over general smart card link destination so user can connect account
	zIndex: 1,
});

const titleBlockHideUnauthorizedStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-smart-element-group] > button': {
		display: 'none',
	},
});

const titleBlockStyles = css({
	marginTop: token('space.0'),
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
		flexShrink: 0,
	},
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a, a:visited, a:focus, a:hover, a:active': {
		textDecoration: 'none',
		pointerEvents: 'auto',
	},
});

const customImageUploadStyles = css({
	position: 'absolute',
	width: '72px',
	top: token('space.0'),
	bottom: token('space.0'),
	left: token('space.0'),
});

type SmallCardProps = CommonCardProps;

export const SmallCard = ({
	contentId,
	cardId,
	link,
	title,
	imageSrc,
	isAvatarShown,
	isPublishDateShown,
	cardDimensions,
	isInViewMode,
	onCardSucceeded,
	onCardFailed,
	imageAltText,
	...analyticsPassThroughProps
}: SmallCardProps) => {
	const cardHeight = cardDimensions.height!;

	const {
		errorCardStatus,
		isNoResult,
		isRestricted,
		isUnauthorized,
		trackAndCategorizeError,
		trackSuccess,
	} = useTrackedSmartCardState(cardId, link, onCardSucceeded, onCardFailed);

	const { showUnsplashImage, showExternalSiteImage, showCustomUploadedImage, hasImage } =
		getImageTypeToShow({
			imageSrc,
		});

	const showCustomTitleUnauthed = isUnauthorized && !!title;

	if (isRestricted || isNoResult) {
		return (
			<CardErrorState
				isRestricted={isRestricted}
				isNoResult={isNoResult}
				link={link}
				cardId={cardId}
				cardSize={CardSize.SMALL}
				cardHeight={cardHeight}
				isInViewMode={isInViewMode}
				errorStatus={errorCardStatus}
				{...analyticsPassThroughProps}
			/>
		);
	}

	return (
		<CommonCardWrapper isInViewMode={isInViewMode}>
			<SmallCardWrapper
				size={CardSize.SMALL}
				cardHeight={cardHeight}
				isPublishDateShown={isPublishDateShown}
				hasImage={hasImage}
				isAvatarShown={isAvatarShown}
			>
				<SmartCardSSR
					id={cardId}
					appearance="inline"
					frameStyle="show"
					platform="web"
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: isInViewMode,
						size: SmartLinkSize.Large,
					}}
					url={link}
					onResolve={trackSuccess}
					onError={trackAndCategorizeError}
				>
					{(showUnsplashImage || showExternalSiteImage) && (
						<PreviewBlock
							ignoreContainerPadding
							placement={MediaPlacement.Left}
							overrideUrl={imageSrc}
						/>
					)}
					{showCustomUploadedImage && (
						<CustomBlock css={customImageUploadStyles}>
							<CustomImageUploadRenderer
								contentId={contentId}
								imageSrc={imageSrc}
								imageAltText={imageAltText}
							/>
						</CustomBlock>
					)}
					<TitleBlock
						hideIcon
						text={title}
						maxLines={1}
						anchorTarget="_self"
						css={[
							titleBlockStyles,
							hasImage && titleBlockImagePaddingStyles,
							showCustomTitleUnauthed && titleBlockHideUnauthorizedStyles,
							!showCustomTitleUnauthed && isUnauthorized && titleBlockUnauthorizedStyles,
						]}
						{...(!!isAvatarShown && { metadata: [{ name: ElementName.OwnedByGroup }] })}
						{...(!!isPublishDateShown && { subtitle: [{ name: ElementName.ModifiedOn }] })}
					/>
				</SmartCardSSR>
			</SmallCardWrapper>
		</CommonCardWrapper>
	);
};
